<script setup lang="ts">
import { ref, type PropType, computed } from 'vue'
import { useVModels } from '@vueuse/core'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import Checkbox from '@/components/ui/checkbox/Checkbox.vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import type {
	ISorting,
	IWarehouseList,
	ICategory,
} from '@/legacy/components/ProductSharingPostList.vue'

const props = defineProps({
	sortMode: {
		type: String,
		default: '',
	},
	filterWarehouses: {
		type: Array as PropType<string[]>,
		default: () => [],
	},
	filterCategories: {
		type: Array as PropType<string[]>,
		default: () => [],
	},
	sortOptions: {
		type: Array as PropType<ISorting[]>,
		default: () => [],
	},
	warehouseOptions: {
		type: Array as PropType<IWarehouseList[]>,
		default: () => [],
	},
	categoryOptions: {
		type: Array as PropType<ICategory[]>,
		default: () => [],
	},
	filterSortTitles: {
		type: Object,
		default: () => {},
	},
	filterSortButtons: {
		type: Object,
		default: () => {},
	},
	isResetDisabled: {
		type: Boolean,
		required: true,
	},
})

const emit = defineEmits([
	'update:sortMode',
	'update:filterWarehouses',
	'update:filterCategories',
	'clearAll',
	'apply',
])
const { sortMode, filterWarehouses, filterCategories } = useVModels(props, emit)

const isCountryListExpanded = ref(false)
const isCategoryListExpanded = ref(false)

const scrollBarClasses = [
	'[scrollbar-gutter:stable]',
	'[&::-webkit-scrollbar]:w-[12px] [&::-webkit-scrollbar]:bg-transparent',
	'[&::-webkit-scrollbar-track]:my-[24px]',
	'[&::-webkit-scrollbar-thumb]:bg-n-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-clip-padding',
	'[&::-webkit-scrollbar-thumb]:border-solid [&::-webkit-scrollbar-thumb]:border-[4px] [&::-webkit-scrollbar-thumb]:border-transparent',
]

const collapsibleItemClass = computed(() => ({
	'hidden group-[.expanded]/warehouse:flex group-[.expanded]/category:flex mdp:flex':
		true,
}))

const shouldCollapseItem = (index: number) => {
	// Only the first 6 items are always visible, with the rest initially collapsed
	const ITEMS_BEFORE_COLLAPSE = 6
	return index >= ITEMS_BEFORE_COLLAPSE
}

const updateFilterSelection = (
	selectedItems: string[],
	itemId: string,
): string[] => {
	const isSelected = selectedItems.includes(itemId)
	return isSelected
		? selectedItems.filter(id => id !== itemId)
		: [...selectedItems, itemId]
}

const toggleWarehouseSelection = (warehouseId: string): void => {
	filterWarehouses.value = updateFilterSelection(
		filterWarehouses.value,
		warehouseId,
	)
}

const toggleCategorySelection = (categorySlug: string): void => {
	filterCategories.value = updateFilterSelection(
		filterCategories.value,
		categorySlug,
	)
}

const handleApply = (): void => {
	emit('apply')
}
</script>

<template>
	<div class="overflow-y-auto p-24px pr-12px" :class="scrollBarClasses">
		<section>
			<div class="typography-h5-str mb-good-friends">
				{{ filterSortTitles.sort }}
			</div>
			<RadioGroup
				class="grid grid-cols-1 gap-24px text-[#191C1F] mdp:grid-cols-2"
				v-model="sortMode"
			>
				<div
					v-for="option in sortOptions"
					:key="option.value"
					class="flex min-w-0 items-center justify-between gap-12px mdp:flex-row-reverse mdp:justify-end mdp:gap-16px"
				>
					<Label
						:for="option.value"
						class="typography-s1-reg w-full cursor-pointer truncate"
					>
						{{ option.label }}
					</Label>
					<RadioGroupItem
						class="flex-shrink-0"
						:id="option.value"
						:value="option.value"
					/>
				</div>
			</RadioGroup>
		</section>

		<hr class="my-24px border-[#E7E9EA]" />

		<section
			class="group/warehouse text-[#191C1F]"
			:class="{ expanded: isCountryListExpanded }"
		>
			<div class="typography-h5-str mb-good-friends">
				{{ filterSortTitles.warehouse }}
			</div>
			<div class="grid grid-cols-1 gap-24px mdp:grid-cols-2">
				<div
					v-for="(option, index) in warehouseOptions"
					:key="option.id"
					class="flex min-w-0 items-center justify-between gap-12px mdp:flex-row-reverse mdp:justify-end mdp:gap-16px"
					:class="[shouldCollapseItem(index) ? collapsibleItemClass : '']"
				>
					<Label
						:for="option.id"
						class="typography-s1-reg flex w-full min-w-0 cursor-pointer items-center gap-12px mdp:gap-16px"
					>
						<img class="h-24px w-24px" :src="option.countryIconUrl" />
						<span class="flex-1 truncate">{{ option.name }}</span>
					</Label>
					<Checkbox
						class="flex-shrink-0"
						:id="option.id"
						:value="option.id"
						:checked="filterWarehouses.includes(option.id)"
						@update:checked="toggleWarehouseSelection(option.id)"
					/>
				</div>
			</div>

			<button
				class="typography-s1-reg mt-24px underline mdp:hidden"
				@click="isCountryListExpanded = !isCountryListExpanded"
			>
				{{
					isCountryListExpanded
						? filterSortButtons.showLess
						: filterSortButtons.showMore
				}}
			</button>
		</section>

		<hr class="my-24px border-[#E7E9EA]" />

		<section
			class="group/category text-[#191C1F]"
			:class="{ expanded: isCategoryListExpanded }"
		>
			<div class="typography-h5-str mb-good-friends">
				{{ filterSortTitles.categories }}
			</div>
			<div class="grid grid-cols-1 gap-24px mdp:grid-cols-2">
				<div
					v-for="(option, index) in categoryOptions"
					:key="option.slug"
					class="flex min-w-0 items-center justify-between gap-12px mdp:flex-row-reverse mdp:justify-end mdp:gap-16px"
					:class="[shouldCollapseItem(index) ? collapsibleItemClass : '']"
				>
					<Label
						:for="option.slug"
						class="typography-s1-reg w-full cursor-pointer truncate"
					>
						{{ option.name }}
					</Label>
					<Checkbox
						class="flex-shrink-0"
						:id="option.slug"
						:value="option.slug"
						:checked="filterCategories.includes(option.slug)"
						@update:checked="toggleCategorySelection(option.slug)"
					/>
				</div>
			</div>

			<button
				class="typography-s1-reg mt-24px underline mdp:hidden"
				@click="isCategoryListExpanded = !isCategoryListExpanded"
			>
				{{
					isCategoryListExpanded
						? filterSortButtons.showLess
						: filterSortButtons.showMore
				}}
			</button>
		</section>
	</div>

	<div
		class="typography-l1-emp mt-auto flex items-center justify-between gap-12px p-20px pt-12px"
	>
		<BaseButton
			size="large"
			variant="ghost"
			class="flex-1 mdp:hidden"
			:disabled="isResetDisabled"
			@click="emit('clearAll')"
		>
			{{ filterSortButtons.reset }}
		</BaseButton>
		<button
			class="typography-s1-reg hidden underline disabled:text-n-300 mdp:block"
			:disabled="isResetDisabled"
			@click="emit('clearAll')"
		>
			{{ filterSortButtons.reset }}
		</button>

		<BaseButton
			size="large"
			variant="pcs-primary"
			class="flex-1 mdp:flex-initial"
			@click="handleApply"
		>
			{{ filterSortButtons.apply }}
		</BaseButton>
	</div>
</template>
